import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenantService } from '../services';
import type {
  CreateTenantRequest,
  Tenant,
  TenantFilters,
  PatchTenantRequest,
} from '../models';
import { mergeTenantDetailCache } from '../utils/tenantDetailCache';
import { useToast } from '@/shared/hooks/useToast';
import { useTranslation } from 'react-i18next';
import type { BackendErrorResponse } from '@/shared';

export const TENANT_QUERY_KEYS = {
  all: ['tenants'] as const,
  lists: () => [...TENANT_QUERY_KEYS.all, 'list'] as const,
  list: (filters?: TenantFilters) =>
    [...TENANT_QUERY_KEYS.lists(), filters] as const,
  detail: (id: string) => [...TENANT_QUERY_KEYS.all, 'detail', id] as const,
  /** Cropped logo data URL after save; survives tenant detail refetch. */
  logoPreview: (id: string) =>
    [...TENANT_QUERY_KEYS.all, 'logo-preview', id] as const,
} as const;

/** List fetch — top-level hook (do not nest inside other hooks’ return objects). */
export function useListTenantsQuery(filters?: TenantFilters) {
  const service = useTenantService();
  return useQuery({
    queryKey: TENANT_QUERY_KEYS.list(filters),
    queryFn: () => service.getTenants(filters),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

export function useTenantByIdQuery(tenantId: string | undefined) {
  const service = useTenantService();
  return useQuery({
    queryKey: TENANT_QUERY_KEYS.detail(tenantId ?? ''),
    queryFn: () => service.getTenantById(tenantId!),
    enabled: !!tenantId,
    refetchOnMount: 'always',
    retry: 1,
  });
}

export function useTenantMutations() {
  const service = useTenantService();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation('tenants');

  const invalidateLists = () =>
    queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.lists() });

  const notification = (
    message: string,
    type: 'info' | 'success' | 'error' | 'warning' | 'message'
  ) => {
    notify(
      message,
      {
        position: 'bottom-right',
        duration: 2000,
      },
      type
    );
  };

  return {
    createTenant: useMutation({
      mutationFn: (payload: CreateTenantRequest) =>
        service.createTenant(payload),
      onSuccess: () => {
        notification(t('tenants.createSuccess'), 'success');
        invalidateLists();
      },
      onError: (error: BackendErrorResponse) => {
        notification(
          error.response?.data?.message || error.message,
          'error'
        );
      },
    }),

    deleteTenant: useMutation({
      mutationFn: (id: string) => service.deleteTenant(id),
      onSuccess: (_, deletedId) => {
        queryClient.removeQueries({
          queryKey: TENANT_QUERY_KEYS.detail(deletedId),
        });
        queryClient.removeQueries({
          queryKey: TENANT_QUERY_KEYS.logoPreview(deletedId),
        });
        notification(t('tenants.deleteSuccess'), 'success');
        invalidateLists();
      },
      onError: (error: BackendErrorResponse) => {
        notification(
          error.response?.data?.message || error.message,
          'error'
        );
      },
    }),

    updateTenant: useMutation({
      mutationFn: ({
        id,
        payload,
      }: {
        id: string;
        payload: PatchTenantRequest;
      }) => service.updateTenant(id, payload),
      onSuccess: (updated, { id, payload }) => {
        notification(t('tenants.updateSuccess'), 'success');
        const logoDataUrl = payload.details?.logo;
        const logoUpdated =
          typeof logoDataUrl === 'string' &&
          logoDataUrl.startsWith('data:image');
        const logoCacheBust = logoUpdated ? Date.now() : undefined;

        if (logoUpdated) {
          queryClient.setQueryData(
            TENANT_QUERY_KEYS.logoPreview(id),
            logoDataUrl
          );
        }

        queryClient.setQueryData<Tenant | undefined>(
          TENANT_QUERY_KEYS.detail(id),
          prev => {
            const merged = mergeTenantDetailCache(prev, updated, payload);
            return logoCacheBust
              ? { ...merged, LogoCacheBust: logoCacheBust }
              : merged;
          }
        );

        void queryClient.invalidateQueries({
          queryKey: TENANT_QUERY_KEYS.detail(id),
        });
        invalidateLists();
      },
      onError: (error: BackendErrorResponse) => {
        notification(
          error.response?.data?.message || error.message,
          'error'
        );
      },
    }),
  };
}
